import { CurrencyCostMapper } from '../impact';
import { ProjectSubType } from '@prisma/client';

export interface LocalProjectAddon {
  id: string;
  projectId: number;
  /** @deprecated Use `prices` for all new work. `price` is the price in this country's currency */
  price: number;
  subscriptionPrice: number;
  prices: CurrencyCostMapper;
  countryNiceCopy: string;
  countryNiceCopyShort: string;
  country: 'US' | 'AU' | 'UK';
  link: string;
}

/**
 * These local projects are only available on the impact shop
 */
export type NonAddonLocalProject = Omit<LocalProjectAddon, 'country'> & {
  country: 'BR';
};

export type LocalProject = LocalProjectAddon | NonAddonLocalProject;

export const USReforestationProject: LocalProjectAddon = {
  id: 'us-reforestation-project-v3',
  projectId: 11465,
  price: 2.73,
  subscriptionPrice: 2.0,
  prices: {
    GBP: 1.85,
    EUR: 2.33,
    USD: 2.73,
    CAD: 3.81,
    AUD: 4.14,
  },
  country: 'US',
  countryNiceCopy: 'the United States',
  countryNiceCopyShort: 'the US',
  link: '/impact/projects/wildfire-restoration-in-mountain-meadows-california',
};

export const AUReforestationProject: LocalProjectAddon = {
  id: 'au-reforestation-project-v3',
  projectId: 11340,
  price: 10.05,
  subscriptionPrice: 7.2,
  prices: {
    GBP: 4.75,
    EUR: 6,
    USD: 6.9,
    CAD: 9.6,
    AUD: 10.05,
  },
  country: 'AU',
  countryNiceCopy: 'Australia',
  countryNiceCopyShort: 'Aus',
  link: '/projects/restoring-degraded-ancestral-land-in-australia',
};

export const UKReforestationProject: LocalProjectAddon = {
  id: 'uk-reforestation-project-v3',
  projectId: 1401,
  price: 5.5,
  subscriptionPrice: 5.0,
  prices: {
    GBP: 5.5,
    EUR: 6.95,
    USD: 8,
    CAD: 11.1,
    AUD: 11.6,
  },
  country: 'UK',
  countryNiceCopy: 'the United Kingdom',
  countryNiceCopyShort: 'the UK',
  link: '/projects/reforestation-projects-in-the-uk',
};

/**
 * Local Brazilian trees are only sold on the impact shop (not as a subscription addon)
 */
export const BRReforestationProject: NonAddonLocalProject = {
  id: '',
  projectId: 7208,
  price: 0,
  subscriptionPrice: 0,
  prices: {
    GBP: 5.45,
    EUR: 6.6,
    USD: 7.6,
    CAD: 10.6,
    AUD: 11.1,
  },
  country: 'BR',
  countryNiceCopy: 'Brazil',
  countryNiceCopyShort: 'Brazil',
  link: '/projects/restoring-degraded-land-in-brazil',
};

export const localProjectsByCountry = {
  UK: UKReforestationProject,
  US: USReforestationProject,
  AU: AUReforestationProject,
  BR: BRReforestationProject,
} as const satisfies Record<string, LocalProject>;

export const localProjectByProjectSubType: Partial<Record<ProjectSubType, LocalProject>> = {
  [ProjectSubType.UK_REFORESTATION]: UKReforestationProject,
  [ProjectSubType.US_REFORESTATION]: USReforestationProject,
  [ProjectSubType.AUSTRALIA_REFORESTATION]: AUReforestationProject,
  [ProjectSubType.BRAZIL_REFORESTATION]: BRReforestationProject,
};

export const isLocalReforestationProjectSubType = (projectSubType?: ProjectSubType | null): boolean =>
  Boolean(projectSubType && localProjectByProjectSubType[projectSubType]);

export type LocalTreeCountry = keyof typeof localProjectsByCountry;

export const projectLocationAddonMapper: {
  readonly [location: string]: LocalProjectAddon;
} = {
  US: USReforestationProject,
  AU: AUReforestationProject,
  GB: UKReforestationProject,
};

/**
 * Includes ids for previous local projects that are no longer sold that but we still need to recognise
 */
export const localProjectIdMapper: {
  readonly [projectId: number]: LocalProject;
} = {
  1364: USReforestationProject,
  1352: AUReforestationProject,
  1401: UKReforestationProject,
  7208: BRReforestationProject,
  11337: USReforestationProject,
  11340: AUReforestationProject,
  11465: USReforestationProject,
};

export const localProjects = [UKReforestationProject, USReforestationProject, AUReforestationProject] as const;

export const localProjectIds = localProjects.map(p => p.id); // strings (uk-reforestation-project)

export const isLocalTreeSubscriptionAddon = (addonId: string): boolean => localProjectIds.includes(addonId);

export const localProjectWPIds = localProjects.map(p => p.projectId); // numbers (1401)

export const isLocalUkProject = (projectId: number) => projectId === UKReforestationProject.projectId;
